import { useAuth0 } from '@auth0/auth0-react';
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { AnalyticsContextValue, AnalyticsUser } from '@systemzero-inc/ui/analytics';
import { PostHogProvider } from './providers/posthog.provider';

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

/**
 * Analytics provider that initializes PostHog and provides tracking methods.
 *
 * Automatically identifies users after Auth0 authentication completes.
 * Uses the provider-agnostic interface from @systemzero-inc/ui/analytics
 * so the underlying analytics service can be swapped without changing
 * call sites.
 *
 * @example
 * ```tsx
 * <AnalyticsProvider>
 *   <App />
 * </AnalyticsProvider>
 * ```
 */
export function AnalyticsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { user, isAuthenticated } = useAuth0();
  const wasAuthenticated = useRef(false);

  const provider = useMemo(() => {
    const apiKey = import.meta.env.VITE_POSTHOG_KEY;
    const apiHost = import.meta.env.VITE_POSTHOG_HOST;
    const uiHost = import.meta.env.VITE_POSTHOG_UI_HOST;
    const environment = import.meta.env.VITE_ENV;

    if (!apiKey || !apiHost) {
      console.warn('PostHog analytics not configured - missing VITE_POSTHOG_KEY or VITE_POSTHOG_HOST');
      return undefined;
    }

    const postHog = new PostHogProvider();
    postHog.init(apiKey, { apiHost, uiHost, environment, service: 'app' });
    return postHog;
  }, []);

  useEffect(() => {
    if (!provider) return;

    if (isAuthenticated && user?.sub) {
      provider.identify({
        id: user.sub,
        organizationId: user.org_id,
      });
      wasAuthenticated.current = true;
    } else if (wasAuthenticated.current && !isAuthenticated) {
      // Only reset when transitioning from authenticated → unauthenticated (logout).
      // Avoids resetting during the initial Auth0 loading state, which would
      // create a new session on every page navigation.
      provider.reset();
      wasAuthenticated.current = false;
    }
  }, [provider, isAuthenticated, user?.sub, user?.org_id]);

  const contextValue: AnalyticsContextValue = useMemo(
    () => ({
      track: (event: string, properties?: Record<string, unknown>) => {
        provider?.track(event, properties);
      },
      page: (name?: string, properties?: Record<string, unknown>) => {
        provider?.page(name, properties);
      },
      identify: (user: AnalyticsUser) => {
        provider?.identify(user);
      },
      reset: () => {
        provider?.reset();
      },
      isReady: !!provider,
    }),
    [provider],
  );

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
}

/**
 * Hook to access analytics tracking methods.
 *
 * Must be called within an AnalyticsProvider. Returns methods to track
 * events, identify users, and reset sessions.
 *
 * @throws Error if called outside AnalyticsProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { track } = useAnalytics();
 *
 *   const handleClick = () => {
 *     track('button_clicked', { buttonId: 'submit' });
 *   };
 * }
 * ```
 */
export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}
